import React, { useContext } from 'react'
import { GridItem } from '@zuto/core/src/grid'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import { AddressDetails, AddressEntryErrors } from '../AddressHistoryContext'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'

interface AddressManualInputProps {
  valueChanged: (name : string, value : string) => void
  postcode?: string
  setPostcode?: React.ChangeEventHandler<HTMLInputElement>
  error: AddressEntryErrors
  existingAnswers?: AddressDetails
  handleKeyPressEvent?: React.KeyboardEventHandler<HTMLInputElement>
}

const AddressManualInput = ({
  valueChanged,
  postcode,
  setPostcode,
  error,
  existingAnswers,
  handleKeyPressEvent
}: AddressManualInputProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const {
    addressRedesignV2
  } = featureSwitches ?? {}

  const setValue = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget
    valueChanged(name, value)
  }

  const setPostcodeValue = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.currentTarget
    valueChanged(name, value)
    if (setPostcode) setPostcode(e)
  }

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.code === 'Enter' || e.key === 'Enter')) {
      if (handleKeyPressEvent) {
        handleKeyPressEvent(e)
      }
    }
  }

  return (
    <>
      <GridItem mt={13} gridColumn={['auto / span 12', '1 / span 12']}>
        <>
          <MuiTextField
            aria-label="label-flatNumber"
            errorMessage={error?.flatNumber}
            id="flatNumber"
            inputMaxLength={50}
            inputType="text"
            inputVariant="outlined"
            label="Flat number"
            mb="34px"
            multipleInputs
            name="flatNumber"
            onChangeHandler={setValue}
            testId="flat-number-input"
            defaultValue={existingAnswers?.flatNumber}
          />
          <MuiTextField
            aria-label="label-buildingName"
            errorMessage={error?.buildingName}
            id="buildingName"
            inputMaxLength={50}
            inputType="text"
            inputVariant="outlined"
            label="Building name"
            mb="34px"
            multipleInputs
            name="buildingName"
            onChangeHandler={setValue}
            testId="building-name-input"
            defaultValue={existingAnswers?.buildingName}
          />
          <MuiTextField
            aria-label="label-buildingNumber"
            errorMessage={error?.buildingNumber}
            id="buildingNumber"
            inputMaxLength={50}
            inputType="text"
            inputVariant="outlined"
            label="Building number"
            mb="34px"
            multipleInputs
            name="buildingNumber"
            onChangeHandler={setValue}
            testId="building-number-input"
            defaultValue={existingAnswers?.buildingNumber}
          />
          <MuiTextField
            aria-label="label-street"
            errorMessage={error?.street}
            id="street"
            inputMaxLength={100}
            inputType="text"
            inputVariant="outlined"
            label="Street name"
            mb="34px"
            multipleInputs
            name="street"
            onChangeHandler={setValue}
            testId="street-input"
            defaultValue={existingAnswers?.street}
          />
          <MuiTextField
            aria-label="label-town"
            errorMessage={error?.town}
            id="town"
            inputMaxLength={100}
            inputType="text"
            inputVariant="outlined"
            label="Town"
            mb="34px"
            multipleInputs
            name="town"
            onChangeHandler={setValue}
            testId="town-input"
            defaultValue={existingAnswers?.town}
          />
          <MuiTextField
            aria-label="label-county"
            errorMessage={error?.county}
            id="county"
            inputMaxLength={100}
            inputType="text"
            inputVariant="outlined"
            label="County"
            mb="34px"
            multipleInputs
            name="county"
            onChangeHandler={setValue}
            testId="county-input"
            defaultValue={existingAnswers?.county}
          />
          <MuiTextField
            aria-label="label-postcode"
            errorMessage={error?.postcode}
            id="postcode"
            inputMaxLength={20}
            inputType="text"
            inputVariant="outlined"
            label="Postcode"
            mb="34px"
            name="postcode"
            onChangeHandler={setPostcodeValue}
            testId="postcode-input"
            {...addressRedesignV2 ? { defaultValue: postcode } : { value: postcode }}
            onKeyPressHandler={handleEnterKeyPress}
          />
        </>
      </GridItem>
    </>
  )
}

export default AddressManualInput
